import moment from "moment-timezone"
import { useReducer, forwardRef, useImperativeHandle, Fragment } from "react"
import { StackView, Divider } from "@planningcenter/tapestry-react"
import { oneOfType, arrayOf, bool, number, shape, string } from "prop-types"
import { isBlank } from "utils/helpers"
import AttendeeTypeAgeFilter from "./SelectionTypeAgeFilter"
import AttendeeTypeGradeFilter from "./SelectionTypeGradeFilter"
import PersonalInfoCheckboxRow from "./PersonalInfoCheckboxRow"
import { GradeFieldLabelSelect, GradeFieldLabelHeader } from "./GradeFieldLabelSelect"

const HIDDEN = "hidden"
const OPTIONAL = "optional"
const REQUIRED = "required"

const ANY_AGE_OR_GRADE = 0
const GRADE_RESTRICTED = 1
const AGE_RESTRICTED = 2

const BIRTHDATE = "birthdate"
const EMERGENCY_CONTACT = "emergency_contact"
const EMAIL = "email"
const PHONE = "phone"
const ADDRESS = "address"
const MEDICAL = "medical"
const GENDER = "gender"
const GRADE = "grade"

const AGE_GRADE_RESTRICTION_UNIT = "AGE_GRADE_RESTRICTION_UNIT"
const AGE_RESTRICTION_PHRASE = "AGE_RESTRICTION_PHRASE"
const AGE_RESTRICTION_UNIT = "AGE_RESTRICTION_UNIT"
const ANY_AGE = "ANY_AGE"
const EVALUATE_AGE_AT = "EVALUATE_AGE_AT"
const GENDER_RESTRICTION_UNIT = "GENDER_RESTRICTION_UNIT"
const GRADE_RESTRICTION_PHRASE = "GRADE_RESTRICTION_PHRASE"
const MAX_AGE = "MAX_AGE"
const MIN_AGE = "MIN_AGE"
const MAX_GRADE = "MAX_GRADE"
const MIN_GRADE = "MIN_GRADE"
const GRADE_LABEL = "GRADE_LABEL"

const EMERGENCY_CONTACT_HELPER_SHELF_MESSAGE =
  "Emergency contact is not saved on the attendee's profile; it is only saved for the registration where it is collected."

const reducer = (state, { type, payload }) => {
  switch (type) {
    case AGE_GRADE_RESTRICTION_UNIT: {
      const { value } = payload
      const nextValue = value ? REQUIRED : OPTIONAL

      let nextCollectionOption = {}
      if (value === AGE_RESTRICTED) {
        nextCollectionOption = {
          birthdate: { ...state.birthdate, value: nextValue },
          minGrade: "",
          maxGrade: "",
          collectionOptions: {
            ...state.collectionOptions,
            birthdate: "R"
          }
        }
      } else if (value === GRADE_RESTRICTED) {
        nextCollectionOption = {
          grade: { ...state.grade, value: nextValue },
          minAge: "",
          maxAge: "",
          evaluateAgeAt: "",
          collectionOptions: {
            ...state.collectionOptions,
            grade: "R"
          }
        }
      } else {
        // ANY_AGE_OR_GRADE
        nextCollectionOption = {
          minAge: "",
          maxAge: "",
          evaluateAgeAt: "",
          minGrade: "",
          maxGrade: ""
        }
      }

      return {
        ...state,
        ageGradeRestrictionUnit: value,
        ...nextCollectionOption
      }
    }
    case AGE_RESTRICTION_PHRASE: {
      const { value } = payload
      let minAge = state.minAge
      let maxAge = state.maxAge

      switch (value) {
        case "exactly": {
          maxAge = state.minAge
          break
        }
        case "at_least": {
          maxAge = ""
          break
        }
        case "at_most": {
          minAge = ""
          break
        }
      }

      return {
        ...state,
        ageRestrictionPhrase: value,
        minAge,
        maxAge
      }
    }
    case AGE_RESTRICTION_UNIT: {
      const { value } = payload

      return {
        ...state,
        ageRestrictionUnit: value
      }
    }
    case BIRTHDATE: {
      const { value } = payload
      const ageGradeRestrictionUnit =
        value === HIDDEN && state.ageGradeRestrictionUnit === AGE_RESTRICTED
          ? ANY_AGE_OR_GRADE
          : state.ageGradeRestrictionUnit

      return {
        ...state,
        ageGradeRestrictionUnit,
        birthdate: { ...state.birthdate, value },
        collectionOptions: {
          ...state.collectionOptions,
          birthdate: value[0].toUpperCase()
        },
        minAge: value === HIDDEN ? "" : state.minAge,
        maxAge: value === HIDDEN ? "" : state.maxAge
      }
    }
    case EMAIL: {
      const { value } = payload

      return {
        ...state,
        email: { ...state.email, value },
        collectionOptions: {
          ...state.collectionOptions,
          email: value[0].toUpperCase()
        }
      }
    }
    case PHONE: {
      const { value } = payload

      return {
        ...state,
        phone: { ...state.phone, value },
        collectionOptions: {
          ...state.collectionOptions,
          phone: value[0].toUpperCase()
        }
      }
    }
    case ADDRESS: {
      const { value } = payload

      return {
        ...state,
        address: { ...state.address, value },
        collectionOptions: {
          ...state.collectionOptions,
          address: value[0].toUpperCase()
        }
      }
    }
    case MEDICAL: {
      const { value } = payload

      return {
        ...state,
        medical: { ...state.medical, value },
        collectionOptions: {
          ...state.collectionOptions,
          medical: value[0].toUpperCase()
        }
      }
    }
    case EMERGENCY_CONTACT: {
      const { value } = payload

      return {
        ...state,
        emergencyContact: { ...state.emergencyContact, value },
        collectionOptions: {
          ...state.collectionOptions,
          emergencyContact: value[0].toUpperCase()
        }
      }
    }
    case EVALUATE_AGE_AT: {
      const { value } = payload

      return {
        ...state,
        evaluateAgeAt: value
      }
    }
    case GENDER: {
      const { value } = payload
      const genderRestrictionUnit = value === HIDDEN ? "" : state.genderRestrictionUnit
      const nextValue = genderRestrictionUnit === "" ? value : REQUIRED

      return {
        ...state,
        gender: { ...state.gender, value: nextValue },
        genderRestrictionUnit,
        collectionOptions: {
          ...state.collectionOptions,
          gender: nextValue[0].toUpperCase()
        }
      }
    }
    case GRADE: {
      const { value } = payload
      const ageGradeRestrictionUnit =
        value === HIDDEN && state.ageGradeRestrictionUnit === GRADE_RESTRICTED
          ? ANY_AGE_OR_GRADE
          : state.ageGradeRestrictionUnit

      return {
        ...state,
        ageGradeRestrictionUnit,
        grade: { ...state.grade, value },
        collectionOptions: {
          ...state.collectionOptions,
          grade: value[0].toUpperCase()
        },
        minGrade: value === HIDDEN ? "" : state.minGrade,
        maxGrade: value === HIDDEN ? "" : state.maxGrade
      }
    }
    case GRADE_RESTRICTION_PHRASE: {
      const { value } = payload

      return {
        ...state,
        gradeRestrictionPhrase: value,
        minGrade: "",
        maxGrade: ""
      }
    }
    case GENDER_RESTRICTION_UNIT: {
      const { value } = payload

      return {
        ...state,
        ...(value === "" ? {} : { gender: { ...state.gender, value: REQUIRED } }),
        collectionOptions: {
          ...state.collectionOptions,
          gender: value === "" ? "O" : "R"
        },
        genderRestrictionUnit: value
      }
    }
    case MAX_AGE: {
      const { value } = payload

      return {
        ...state,
        maxAge: value
      }
    }
    case MIN_AGE: {
      const { value } = payload

      return {
        ...state,
        minAge: value
      }
    }
    case MAX_GRADE: {
      const { value } = payload

      return {
        ...state,
        maxGrade: value
      }
    }
    case MIN_GRADE: {
      const { value } = payload
      const nextMaxGrade = state.gradeRestrictionPhrase === "only" ? value : state.maxGrade

      return {
        ...state,
        minGrade: value,
        maxGrade: nextMaxGrade
      }
    }
    case GRADE_LABEL: {
      const { value } = payload
      return {
        ...state,
        gradeLabel: value
      }
    }
  }

  throw Error("Unknown action: " + type)
}

const initialState = ({
  evaluateAgeAt,
  ageRestrictionUnit,
  genderRestrictionUnit,
  maxAge,
  minAge,
  maxGrade,
  minGrade,
  ageGradeRestrictionUnit,
  ageRestrictionPhrase,
  gradeRestrictionPhrase,
  gradeLabel,
  eventStartDate,
  personalCollectionOptionAttributes: {
    gender,
    grade,
    birthdate,
    email = null,
    phone = null,
    address = null,
    medical = null,
    emergencyContact
  }
}) => {
  let initialEvaluateAgeAt = moment().format()
  if (!isBlank(evaluateAgeAt)) {
    initialEvaluateAgeAt = evaluateAgeAt
  } else if (!isBlank(eventStartDate)) {
    initialEvaluateAgeAt = moment(eventStartDate, window.momentDateFormat).format()
  }

  return {
    ageGradeRestrictionUnit,
    ageRestrictionPhrase: ageRestrictionPhrase || "between",
    ageRestrictionUnit: ageRestrictionUnit || 0,
    birthdate,
    email,
    phone,
    address,
    medical,
    emergencyContact,
    evaluateAgeAt: initialEvaluateAgeAt,
    gender,
    genderRestrictionUnit: isBlank(genderRestrictionUnit) ? "" : genderRestrictionUnit,
    grade,
    gradeLabel,
    gradeRestrictionPhrase: gradeRestrictionPhrase || "between",
    minAge: isBlank(minAge) ? "" : minAge,
    maxAge: isBlank(maxAge) ? "" : maxAge,
    minGrade: isBlank(minGrade) ? "" : minGrade,
    maxGrade: isBlank(maxGrade) ? "" : maxGrade,
    collectionOptions: {
      birthdate: birthdate.value[0].toUpperCase(),
      gender: gender.value[0].toUpperCase(),
      grade: grade.value[0].toUpperCase(),
      email: email?.value[0].toUpperCase(),
      phone: phone?.value[0].toUpperCase(),
      address: address?.value[0].toUpperCase(),
      medical: medical?.value[0].toUpperCase(),
      emergencyContact: emergencyContact.value[0].toUpperCase()
    }
  }
}

const PersonalInfoOptionsAndRestrictions = forwardRef(
  (
    {
      selectionType,
      selectionType: { genderOptions, gradeOptions, hasCustomGenders, gradeLabelOptions },
      signup: { firstEventDate: eventStartDate }
    },
    ref
  ) => {
    const [state, dispatch] = useReducer(
      reducer,
      initialState({
        ...selectionType,
        eventStartDate
      })
    )

    const {
      ageGradeRestrictionUnit,
      ageRestrictionPhrase,
      ageRestrictionUnit,
      birthdate,
      email,
      phone,
      address,
      medical,
      emergencyContact,
      evaluateAgeAt,
      gender,
      genderRestrictionUnit,
      grade,
      gradeRestrictionPhrase,
      maxAge,
      minAge,
      maxGrade,
      minGrade,
      gradeLabel
    } = state

    useImperativeHandle(ref, () => ({
      getOptionsAndRestrictionState: () => {
        const {
          minGrade,
          maxGrade,
          minAge,
          maxAge,
          collectionOptions,
          genderRestrictionUnit,
          gradeLabel,
          ageRestrictionUnit,
          evaluateAgeAt
        } = state || {}
        return {
          minGrade,
          maxGrade,
          minAge,
          maxAge,
          collectionOptions,
          genderRestrictionUnit,
          gradeLabel,
          ageRestrictionUnit,
          evaluateAgeAt
        }
      }
    }))

    const isRequiredForced = attribute => {
      switch (attribute) {
        case BIRTHDATE: {
          return ageGradeRestrictionUnit === AGE_RESTRICTED
        }
        case GENDER: {
          return !!genderRestrictionUnit
        }
        case GRADE: {
          return ageGradeRestrictionUnit === GRADE_RESTRICTED
        }
      }
    }

    const personal_info_fields = [
      gender,
      birthdate,
      grade,
      emergencyContact,
      email,
      phone,
      address,
      medical
    ]

    return (
      <>
        <div>
          <h4 className="label">Available to</h4>
          <div>
            <select
              name="attendee_type[gender_restriction_unit]"
              id="attendee_type_gender_restriction_unit"
              className="select select--inline mb-1"
              value={genderRestrictionUnit}
              onChange={({ target: { value } }) =>
                dispatch({ type: GENDER_RESTRICTION_UNIT, payload: { value } })
              }
            >
              {genderOptions.map(option => (
                <option key={option[1] || ""} value={option[1] || ""}>
                  {option[0]}
                </option>
              ))}
            </select>
          </div>
          {hasCustomGenders && genderRestrictionUnit !== "" && (
            <div className="info-alert alert mb-1">
              If someone has selected a custom gender option, they will also be able to register for
              this selection.
            </div>
          )}
          <div>
            <select
              id="attendee_type_age_grade_restriction_unit_ga"
              name="attendee_type[age_grade_restriction_unit]"
              className="select select--inline mb-1"
              value={ageGradeRestrictionUnit}
              onChange={({ target: { value } }) =>
                dispatch({
                  type: AGE_GRADE_RESTRICTION_UNIT,
                  payload: { value: Number(value) }
                })
              }
            >
              <option key={ANY_AGE_OR_GRADE} value={ANY_AGE_OR_GRADE}>
                All ages and grades
              </option>
              <option key={AGE_RESTRICTED} value={AGE_RESTRICTED}>
                Certain ages
              </option>
              <option key={GRADE_RESTRICTED} value={GRADE_RESTRICTED}>
                Certain grades
              </option>
            </select>
          </div>
          {ageGradeRestrictionUnit === AGE_RESTRICTED && (
            <AttendeeTypeAgeFilter
              ageRestrictionPhrase={ageRestrictionPhrase}
              ageRestrictionUnit={ageRestrictionUnit}
              evaluateAgeAt={evaluateAgeAt}
              maxAge={maxAge}
              minAge={minAge}
              onAgeRestrictionPhraseChange={({ target: { value } }) =>
                dispatch({ type: AGE_RESTRICTION_PHRASE, payload: { value } })
              }
              onAgeRestrictionUnitChange={({ target: { value } }) =>
                dispatch({ type: AGE_RESTRICTION_UNIT, payload: { value: Number(value) } })
              }
              onAnyAgeChange={({ target: { value } }) =>
                dispatch({ type: ANY_AGE, payload: { value } })
              }
              onEvaluateAgeAtChange={date =>
                dispatch({ type: EVALUATE_AGE_AT, payload: { value: date } })
              }
              onMaxAgeChange={({ target: { value } }) =>
                dispatch({ type: MAX_AGE, payload: { value: Number(value) } })
              }
              onMinAgeChange={({ target: { value } }) =>
                dispatch({ type: MIN_AGE, payload: { value: Number(value) } })
              }
            />
          )}
          {grade.value !== HIDDEN && ageGradeRestrictionUnit !== GRADE_RESTRICTED && (
            <div className="mb-1">
              <GradeFieldLabelSelect
                gradeLabelOptions={gradeLabelOptions}
                gradeLabel={gradeLabel}
                onSelect={({ target: { value } }) =>
                  dispatch({ type: GRADE_LABEL, payload: { value } })
                }
              />
            </div>
          )}
          {ageGradeRestrictionUnit === GRADE_RESTRICTED && (
            <>
              <GradeFieldLabelHeader />
              <AttendeeTypeGradeFilter
                gradeRestrictionPhrase={gradeRestrictionPhrase}
                gradeOptions={gradeOptions}
                maxGrade={maxGrade}
                minGrade={minGrade}
                onGradeRestrictionPhraseChange={({ target: { value } }) => {
                  dispatch({ type: GRADE_RESTRICTION_PHRASE, payload: { value } })
                }}
                onMaxGradeChange={({ target: { value } }) =>
                  dispatch({ type: MAX_GRADE, payload: { value: Number(value) } })
                }
                onMinGradeChange={({ target: { value } }) =>
                  dispatch({ type: MIN_GRADE, payload: { value: Number(value) } })
                }
              >
                <GradeFieldLabelSelect
                  gradeLabelOptions={gradeLabelOptions}
                  showHeader={false}
                  gradeLabel={gradeLabel}
                  onSelect={({ target: { value } }) =>
                    dispatch({ type: GRADE_LABEL, payload: { value } })
                  }
                />
              </AttendeeTypeGradeFilter>
            </>
          )}
        </div>
        <Divider css={{ width: "calc(100% + 2rem)", marginLeft: "-1rem" }} />
        <div css={{ marginBottom: "-1rem" }} data-pendo="selection-personalInfo">
          <StackView axis="horizontal" distribution="space-between" marginTop={1}>
            <h4 className="label">Personal information to collect</h4>
            <h4 className="label">Required</h4>
          </StackView>
          <StackView spacing={2} marginVertical={2}>
            {personal_info_fields.map(({ attribute, text, value }, index) => {
              const forceRequired = isRequiredForced(attribute)

              return (
                <Fragment key={attribute}>
                  <PersonalInfoCheckboxRow
                    attribute={attribute}
                    text={text}
                    value={value}
                    forceRequired={forceRequired}
                    emergencyHelperShelfMessage={EMERGENCY_CONTACT_HELPER_SHELF_MESSAGE}
                    onSelect={value => dispatch({ type: attribute, payload: { value } })}
                  />
                  {index !== personal_info_fields.length - 1 ? <Divider /> : <></>}
                </Fragment>
              )
            })}
          </StackView>
        </div>
      </>
    )
  }
)

PersonalInfoOptionsAndRestrictions.displayName = "PersonalInfoOptionsAndRestrictions"

const collectionOptionPropType = shape({
  attribute: string.isRequired,
  text: string.isRequired,
  value: string.isRequired
}).isRequired

const genderOption = arrayOf(string).isRequired
const gradeOption = arrayOf(oneOfType([string, number])).isRequired

PersonalInfoOptionsAndRestrictions.propTypes = {
  signup: shape({
    firstEventDate: string
  }),
  selectionType: shape({
    ageRestrictionUnit: number,
    evaluateAgeAt: string,
    genderRestrictionUnit: string.isRequired,
    maxAge: number,
    minAge: number,
    maxGrade: number,
    minGrade: number,
    name: string.isRequired,
    basePriceString: string.isRequired,
    ageGradeRestrictionUnit: number.isRequired,
    ageRestrictionPhrase: string.isRequired,
    genderOptions: arrayOf(genderOption).isRequired,
    gradeLabel: string.isRequired,
    gradeLabelOptions: arrayOf(string).isRequired,
    gradeOptions: arrayOf(gradeOption).isRequired,
    gradeRestrictionPhrase: string.isRequired,
    hasCustomGenders: bool.isRequired,
    personalCollectionOptionAttributes: shape({
      birthdate: collectionOptionPropType,
      emergencyContact: collectionOptionPropType,
      gender: collectionOptionPropType,
      grade: collectionOptionPropType
    })
  }).isRequired
}

export default PersonalInfoOptionsAndRestrictions
